/**
 * useNotificationRefresh Hook
 *
 * Refreshes notification unread count on route navigation with 60-second deduplication.
 * Prevents excessive API calls by checking the last fetch time before making a new request.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../types';
import { setUnreadCount, updateLastFetchTime } from '../features/notifications/notificationsSlice';
import { notificationsClient } from '../apis/client-instances';

const DEDUPE_INTERVAL_MS = 60 * 1000; // 60 seconds

export function useNotificationRefresh() {
  const location = useLocation();
  const dispatch = useDispatch();
  const lastFetchTime = useSelector((state: RootState) => state.notifications.lastFetchTime);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      const now = Date.now();

      // Skip if we fetched within the last 60 seconds
      if (lastFetchTime && now - lastFetchTime < DEDUPE_INTERVAL_MS) {
        return;
      }

      try {
        const response = await notificationsClient.getUnreadCount();
        const data = await response.data.text();
        const parsed = JSON.parse(data);
        dispatch(setUnreadCount(parsed.unreadCount || 0));
        dispatch(updateLastFetchTime());
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
  }, [location.pathname]); // Trigger on route change
}
